import { type Registry as Services, service } from '@ember/service';
import { PRISM_SPACING_100 } from '@mylighthouse/prism-foundation';
import { not } from 'ember-truth-helpers';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralTooltipsTooltipOtb from 'lumen/components/general/tooltips/tooltip-otb';
import HotelMarketInsightChartsBaseChartOtbBase from 'lumen/components/hotel/market-insight/charts/base/chart-otb-base';
import {
  SHORT_TERM_RENTAL_OTB_COLOR,
  TOTAL_OTB,
} from 'lumen/utils/constants/chart-color-constants';

export default class HotelMarketInsightChartsChartOtbOverview extends HotelMarketInsightChartsBaseChartOtbBase {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const chartSeries = [];

    const otbSeries = {
      type: 'column',
      name: this.intl.t('rates.my_OTB'),
      data: [],
      color: TOTAL_OTB,
      borderWidth: 1,
      borderColor: 'var(--prism-color-elevation-surface)',
    };

    const otbBenchmarkSeries = {
      type: 'area',
      name: this.intl.t('rates.tooltip.market_otb'),
      data: [],
      color: 'var(--prism-color-chart-accent-primary-figure-default)',
      opacity: 1,
      fillOpacity: 0.15,
      borderWidth: 1,
      marker: { enabled: false },
    };

    const otbShortTermRentalSeries = {
      type: 'column',
      name: this.intl.t('short_term_rental.otb'),
      data: [],
      color: SHORT_TERM_RENTAL_OTB_COLOR,
      borderWidth: 1,
      marker: { enabled: false },
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.sortedDays.forEach((day) => {
      // Use occupancy for day aggregation, use weeklyOccupancy for week aggregation
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const occupancy = this.args.isDailyOverview
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          day.occupancy
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (day.weeklyOccupancy ?? day.occupancy);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const x = this.hotelAndMarketService.toLocalUnixTime(day.date.unix());

      if (!occupancy) {
        return;
      }

      if (this.access.hasOTB) {
        // @ts-expect-error: Incorrect type
        otbSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: occupancy.occupancyPercentage,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          day: occupancy.day,
        });
      }

      if (this.access.hasMarketOtb) {
        // @ts-expect-error: Incorrect type
        otbBenchmarkSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: occupancy.benchmarkValueNormalized,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          day: occupancy.day,
        });
      }

      if (this.showShortTermRental) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const shortTermRentalOccupancy = (day.rentalOccupancies ?? [])[0];

        if (!shortTermRentalOccupancy) {
          return;
        }

        // @ts-expect-error: Incorrect type
        otbShortTermRentalSeries.data.push({
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: shortTermRentalOccupancy.value,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          day,
        });
      }
    });

    if (this.access.hasOTB) {
      chartSeries.push(otbSeries);
    }

    if (this.access.hasMarketOtb) {
      chartSeries.push(otbBenchmarkSeries);
    }

    if (this.showShortTermRental) {
      chartSeries.push(otbShortTermRentalSeries);
    }

    return chartSeries;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    const chartOptions = super.chartOptions;
    // @ts-expect-error: Incorrect type
    chartOptions.yAxis.min = 0;
    // @ts-expect-error: Incorrect type
    chartOptions.yAxis.max = 1;
    // @ts-expect-error: Incorrect type
    chartOptions.title = {
      text: 'chart-my-otb-overview',
    };
    if (this.showShortTermRental) {
      chartOptions.plotOptions = {
        column: {
          // @ts-expect-error: Incorrect type
          groupPadding: 0.25,
        },
      };
    }

    // Sets the styling options to align the chart with the main Rate Insight chart
    // @ts-expect-error: Incorrect type
    if (this.args.alignWithRatesChart) {
      chartOptions.chart.marginLeft = 110;
      chartOptions.chart.marginTop = PRISM_SPACING_100;
      // @ts-expect-error: Incorrect type
      chartOptions.chart.marginRight = 77.5;
      // @ts-expect-error: Incorrect type
      chartOptions.plotOptions.column.pointWidth = 10;
      // @ts-expect-error: Incorrect type
      delete chartOptions.xAxis.tickPositioner;

      // @ts-expect-error: Incorrect type
      chartOptions.xAxis.labels.rotation = -45;
    }

    return chartOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showShortTermRental() {
    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab &&
      // @ts-expect-error: Incorrect type
      this.args.alignWithRatesChart
    ) {
      return true;
    }

    return false;
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="hidden" id="dummy-tooltip-for-market-otb">
      <div class="tooltip-otb-container">
        {{! @glint-expect-error: Incorrect type }}
        <GeneralTooltipsTooltipOtb
          {{! @glint-expect-error: Incorrect type }}
          @occupancy={{this.tooltipOccupancy}}
          {{! @glint-expect-error: Incorrect type }}
          @rentalOccupancy={{this.tooltipShortTermRentalOccupancy}}
          @showAvailableRooms={{true}}
          @showShortTermRental={{this.showShortTermRental}}
          {{! @glint-expect-error: Incorrect type }}
          @showUpdateTime={{not this.tooltipOccupancy.linkedDayIsPast}}
        />
      </div>
    </div>
  </template>
}
